import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon, Description as ApiIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PagedDoc } from '../components/PagedDoc';
import { useAuth } from '../contexts/AuthContext';

/**
 * Renders GUIDE.md — what each page does and the things that are easy to get wrong.
 *
 * Kept as markdown in public/ rather than as JSX so it can be edited without touching React.
 * Shown one section at a time with contents, links and search by PagedDoc, which the API docs
 * share, so a fix to one benefits both.
 */
export const Guide: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [source, setSource] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/GUIDE.md')
      .then(res => {
        if (!res.ok) throw new Error(`${res.status}`);
        return res.text();
      })
      .then(text => {
        // A missing file is served as index.html by the SPA fallback, which would otherwise
        // render as a wall of HTML
        if (text.trimStart().startsWith('<')) throw new Error('not found');
        setSource(text);
      })
      .catch(() => setError('Could not load the user guide.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1">User Guide</Typography>
          <Typography variant="body2" color="text.secondary">
            What each page does, and the things that are easy to get wrong.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {user?.role !== 'demo' && (
            <Button size="small" startIcon={<ApiIcon />} onClick={() => navigate('/api-documentation')}>
              API Docs
            </Button>
          )}
          <Button
            size="small"
            startIcon={<OpenInNewIcon />}
            href="/GUIDE.md"
            target="_blank"
            rel="noopener noreferrer"
          >
            Raw markdown
          </Button>
        </Box>
      </Box>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <PagedDoc
          source={source}
          basePath="/guide"
          noun="the guide"
          // The tagline is already the page subtitle above
          introOmit={[/^What each page does, and the things that are easy to get wrong\.\n+/]}
        />
      )}
    </Box>
  );
};
